import React from 'react'
import { FlumeConfig, Controls, Colors } from 'flume'
import { getFieldsByType, getNumBusinessRules } from 'utilities'
import format from 'date-fns/format'
import store from '../../../store'
import BusinessRulesButton from '../ValidationEditor'
import CalendarInput from '../CalendarInput'

const getFieldOptionsByType = (types = [], context) =>
  (getFieldsByType(types, store.getState().designer.wizard.fields) || [])
    .filter((field) => {
      const isNotHostField = context.allowCurrentField
        ? true
        : context.hostFieldId !== field.fieldId
      const parentIsNotRepeater = field.parent.type.toUpperCase() !== 'REPEATER'
      if (context.allowCurrentRepeaterFields) {
        const isInCurrentRepeater = field.parent.id === context.parentFieldId
        return isNotHostField && (parentIsNotRepeater ? true : isInCurrentRepeater)
      } else {
        return isNotHostField && parentIsNotRepeater
      }
    })
    .map((field) => ({
      label: field.label || 'Unlabeled Field',
      value: field.fieldId,
      description: `${field.name} - ${field.fieldClass}`,
    }))

const config = new FlumeConfig()
config
  .addPortType({
    type: 'string',
    label: 'Text',
    name: 'string',
    color: Colors.green,
    controls: [Controls.text({ name: 'string' })],
  })
  .addPortType({
    type: 'number',
    label: 'Number',
    name: 'number',
    color: Colors.red,
    controls: [Controls.number({ name: 'number' })],
  })
  .addPortType({
    type: 'boolean',
    label: 'True/False',
    name: 'boolean',
    color: Colors.blue,
    controls: [Controls.checkbox({ name: 'boolean', label: 'True/False' })],
  })
  .addPortType({
    type: 'fieldId',
    label: 'Field',
    name: 'fieldId',
    color: Colors.grey,
    controls: [
      Controls.select({
        name: 'fieldId',
        placeholder: '[Pick a Field]',
        getOptions: (_, context) => {
          const fields = store.getState().designer.wizard.fields
          const options = Object.values(fields)
            .filter(
              (field) =>
                context.hostFieldId !== field.fieldId &&
                field.parent.type.toUpperCase() !== 'REPEATER'
            )
            .map((field) => ({
              label: field.label || 'Unlabeled Field',
              value: field.fieldId,
              description: `${field.name} - ${field.fieldClass}`,
            }))
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'optionFieldId',
    label: 'Field',
    name: 'optionFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'optionFieldId',
        placeholder: '[Select a Field]',
        setValue: (newData, oldData) => {
          if (newData.optionFieldId.optionFieldId !== oldData.optionFieldId.optionFieldId) {
            return {
              ...newData,
              fieldOptionValues: {
                ...newData.fieldOptionValues,
                values: [],
              },
            }
          } else {
            return newData
          }
        },
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(
            [
              'select',
              'radio',
              'serverSelect',
              'serverRadio',
              'MultiComplete',
              'ServerMultiComplete',
            ],
            context
          )
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'fieldOptionValues',
    label: 'Selected Option(s)',
    name: 'fieldOptionValues',
    color: Colors.yellow,
    hidePort: true,
    controls: [
      Controls.multiselect({
        name: 'values',
        label: 'Possible Values',
        getOptions: (inputData) => {
          const fieldId = inputData.optionFieldId.optionFieldId
          const field = store.getState().designer.wizard.fields[fieldId]
          if (!field) return []

          let options = field.options

          if (['serverSelect', 'serverRadio'].includes(field.type)) {
            const serverOptionsCache = store.getState().designer.serverOptionsCache
            const cachedOptions = serverOptionsCache[`${field.optionsSrc}/${field.subOptionsSrc}`]
            options = [{ value: '', label: '[No Selection]' }, ...(cachedOptions || [])]
          }

          return options.map((option) => ({
            label: option.label,
            value: option.value,
            description: option.value,
          }))
        },
      }),
    ],
  })
  .addPortType({
    type: 'checkboxFieldId',
    label: 'Field',
    name: 'checkboxFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'checkboxFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['checkbox', 'dependentCheck'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'textboxFieldId',
    label: 'Field',
    name: 'textboxFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'textboxFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['text', 'textArea'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'uploadAndSubmitFieldId',
    label: 'Field',
    name: 'uploadAndSubmitFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'uploadAndSubmitFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['UploadAndSubmit'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'uploadFieldId',
    label: 'Field',
    name: 'uploadFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'uploadFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['file'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'dateFieldId',
    label: 'Date Field',
    name: 'dateFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'dateFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['date'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'entityNameFieldId',
    label: 'Entity Name Field',
    name: 'entityNameFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'entityNameFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['EntityNameSearch'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'nameQualifiedOrgFieldId',
    label: 'Name Qualified Org Field',
    name: 'nameQualifiedOrgFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'nameQualifiedOrgFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['NameQualifiedOrg'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'repeaterFieldId',
    label: 'Repeater',
    name: 'repeaterFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'repeaterFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['repeater'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'formationLocaleFieldId',
    label: 'Formation Locale Field',
    name: 'formationLocaleFieldId',
    color: Colors.grey,
    hidePort: true,
    controls: [
      Controls.select({
        name: 'formationLocaleFieldId',
        placeholder: '[Select a Field]',
        getOptions: (_, context) => {
          const options = getFieldOptionsByType(['FormationLocale'], context)
          return options
        },
      }),
    ],
  })
  .addPortType({
    type: 'checkboxState',
    label: 'Checkbox State',
    name: 'checkboxState',
    hidePort: true,
    controls: [
      Controls.select({
        name: 'checkboxState',
        placeholder: '[Checked or Unchecked]',
        options: [
          { value: 'checked', label: 'Checked' },
          { value: 'unchecked', label: 'Unchecked' },
        ],
        defaultValue: 'checked',
      }),
    ],
  })
  .addPortType({
    type: 'businessRules',
    label: 'Business Rules',
    name: 'businessRules',
    color: Colors.purple,
    controls: [
      Controls.custom({
        name: 'businessRules',
        defaultValue: [],
        label: 'Business Rules',
        render: (data, onChange, context) => (
          <BusinessRulesButton
            field={{
              ...context.field,
              businessRules: data,
            }}
            numRules={getNumBusinessRules(data)}
            handleChange={(_, rules) => onChange(rules)}
            buttonClassName="btn flume-button"
          />
        ),
      }),
    ],
  })
  .addPortType({
    type: 'booleanAppSetting',
    label: 'True/False App Setting',
    name: 'booleanAppSetting',
    hidePort: true,
    controls: [
      Controls.select({
        name: 'booleanAppSetting',
        defaultValue: '',
        label: 'True/False App Setting',
        getOptions: () => {
          const types = store.getState().appSettingTypes.boolean || []
          return types.map((type) => ({
            value: type.NAME,
            label: type.NAME,
            description: type.DESCR,
          }))
        },
      }),
    ],
  })
  .addPortType({
    type: 'stringAppSetting',
    label: 'Text App Setting',
    name: 'stringAppSetting',
    hidePort: true,
    controls: [
      Controls.select({
        name: 'stringAppSetting',
        defaultValue: '',
        label: 'Text App Setting',
        getOptions: () => {
          const types = store.getState().appSettingTypes.string || []
          return types.map((type) => ({
            value: type.NAME,
            label: type.NAME,
            description: type.DESCR,
          }))
        },
      }),
    ],
  })
  .addPortType({
    type: 'numberAppSetting',
    label: 'Number App Setting',
    name: 'numberAppSetting',
    hidePort: true,
    controls: [
      Controls.select({
        name: 'numberAppSetting',
        defaultValue: '',
        label: 'Number App Setting',
        getOptions: () => {
          const types = store.getState().appSettingTypes.number || []
          return types.map((type) => ({
            value: type.NAME,
            label: type.NAME,
            description: type.DESCR,
          }))
        },
      }),
    ],
  })
  .addPortType({
    type: 'date',
    label: 'Date',
    name: 'date',
    color: Colors.pink,
    controls: [
      Controls.custom({
        name: 'date',
        defaultValue: format(new Date(), 'MM/dd/yyyy'),
        label: 'Date',
        render: (data, onChange, context, triggerRecalculation, control) => {
          return (
            <div onMouseDown={(e) => e.stopPropagation()}>
              <CalendarInput
                label={control.inputLabel}
                value={data}
                onChange={onChange}
                inputDisabled
              />
            </div>
          )
        },
      }),
    ],
  })
  .addPortType({
    type: 'globalBoolean',
    name: 'globalBoolean',
    label: 'Global True/False Variable',
    controls: [
      Controls.select({
        label: 'Global Variable',
        name: 'globalBoolean',
        placeholder: '[Pick a Variable]',
        getOptions: (_, context) => {
          const booleans = store.getState().designer.wizard.globalVariables.boolean
          return booleans.order
            .filter((id) => context.currentId !== id)
            .map((id) => ({
              label: booleans.variables[id].label || 'Unlabeled Variable',
              value: id,
            }))
        },
      }),
    ],
  })
  .addPortType({
    type: 'globalString',
    name: 'globalString',
    label: 'Global Text Variable',
    controls: [
      Controls.select({
        label: 'Global Variable',
        name: 'globalString',
        placeholder: '[Pick a Variable]',
        getOptions: (_, context) => {
          const strings = store.getState().designer.wizard.globalVariables.string
          return strings.order
            .filter((id) => context.currentId !== id)
            .map((id) => ({
              label: strings.variables[id].label || 'Unlabeled Variable',
              value: id,
            }))
        },
      }),
    ],
  })
  .addPortType({
    type: 'globalNumber',
    name: 'globalNumber',
    label: 'Global Number Variable',
    controls: [
      Controls.select({
        label: 'Global Variable',
        name: 'globalNumber',
        placeholder: '[Pick a Variable]',
        getOptions: (_, context) => {
          const numbers = store.getState().designer.wizard.globalVariables.number
          return numbers.order
            .filter((id) => context.currentId !== id)
            .map((id) => ({
              label: numbers.variables[id].label || 'Unlabeled Variable',
              value: id,
            }))
        },
      }),
    ],
  })
  .addNodeType({
    type: 'output',
    label: 'Field Attributes',
    addable: false,
    deletable: false,
    initialWidth: 200,
    root: true,
    inputs: (ports) => [
      ports.string({ name: 'label', label: 'Label', noControls: true }),
      ports.string({ name: 'description', label: 'Description', noControls: true }),
      ports.string({ name: 'tooltip', label: 'Tooltip', noControls: true }),
      ports.boolean({ name: 'active', label: 'Visible', noControls: true }),
      ports.boolean({ name: 'disabled', label: 'Disabled', noControls: true }),
      ports.boolean({
        name: 'disabledDuringCorrection',
        label: 'Disabled During Corrections',
        noControls: true,
      }),
      ports.boolean({ name: 'required', label: 'Show Required Indicator', noControls: true }),
      ports.boolean({
        name: 'includeInSummary',
        label: 'Include in Summary',
        noControls: true,
      }),
      ports.boolean({
        name: 'hideFieldOnForm',
        label: 'Hide Field on Form',
        noControls: true,
      }),
      ports.boolean({
        name: 'hideLabelOnForm',
        label: 'Hide Label on Form',
        noControls: true,
      }),
      ports.boolean({
        name: 'dynamic',
        label: 'Dynamic Data',
        noControls: true,
      }),
      ports.boolean({
        name: 'redact',
        label: 'Redact',
        noControls: true,
      }),
      ports.businessRules({ name: 'businessRules', label: 'Business Rules', noControls: true }),
    ],
  })
  .addNodeType({
    type: 'booleanOutput',
    label: 'True/False Variable',
    initialWidth: 170,
    root: true,
    addable: false,
    deletable: false,
    inputs: (ports) => [ports.boolean({ name: 'value' })],
  })
  .addNodeType({
    type: 'stringOutput',
    label: 'Text Variable',
    initialWidth: 170,
    root: true,
    addable: false,
    deletable: false,
    inputs: (ports) => [ports.string({ name: 'value' })],
  })
  .addNodeType({
    type: 'numberOutput',
    label: 'Number Variable',
    initialWidth: 170,
    root: true,
    addable: false,
    deletable: false,
    inputs: (ports) => [ports.number({ name: 'value' })],
  })
  .addNodeType({
    type: 'businessRuleOutput',
    label: 'Business Rule',
    initialWidth: 170,
    root: true,
    addable: false,
    deletable: false,
    inputs: (ports) => [
      ports.boolean({ name: 'isValid', label: 'Is Valid' }),
      ports.string({ name: 'errorMessage', label: 'Error Message' }),
    ],
  })
  .addNodeType({
    type: 'wizardAttributesOutput',
    label: 'Wizard Attributes',
    initialWidth: 170,
    root: true,
    addable: false,
    deletable: false,
    inputs: (ports) => [
      ports.string({
        name: 'formTitle',
        label: 'Form Title',
        noControls: true,
      }),
      ports.string({
        name: 'feeDescription',
        label: 'Fee Description',
        noControls: true,
      }),
      ports.boolean({
        name: 'internalReviewRequired',
        label: 'Internal review is required',
        noControls: true,
      }),
    ],
  })
  .addNodeType({
    type: 'field',
    label: 'Field',
    description: 'Represents a field in the wizard',
    initialWidth: 210,
    sortIndex: 0,
    inputs: (ports) => [ports.fieldId({ hidePort: true })],
    outputs: (ports) => [
      ports.string({ name: 'label', label: 'Label' }),
      ports.string({ name: 'description', label: 'Description' }),
      ports.string({ name: 'tooltip', label: 'Tooltip' }),
      ports.boolean({ name: 'active', label: 'Visible' }),
      ports.boolean({ name: 'disabled', label: 'Disabled' }),
      ports.boolean({
        name: 'disabledDuringCorrection',
        label: 'Disabled During Corrections',
      }),
      ports.boolean({ name: 'required', label: 'Show Required Indicator' }),
      ports.boolean({
        name: 'includeInSummary',
        label: 'Include in Summary',
      }),
      ports.boolean({
        name: 'hideFieldOnForm',
        label: 'Hide Field on Form',
      }),
      ports.boolean({
        name: 'hideLabelOnForm',
        label: 'Hide Label on Form',
      }),
      ports.boolean({
        name: 'dynamic',
        label: 'Dynamic Data',
      }),
      ports.boolean({
        name: 'redact',
        label: 'Redact',
      }),
    ],
  })
  .addNodeType({
    type: 'valueEqualsOneOfOptions',
    label: 'Equals or includes one of options',
    description: 'Outputs if a value equals or includes one of options',
    initialWidth: 210,
    sortIndex: 1,
    inputs: (ports) => [ports.optionFieldId(), ports.fieldOptionValues()],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'checkboxIsInState',
    label: 'Checkbox is in State',
    description: 'Outputs if a checkbox is checked or unchecked',
    initialWidth: 210,
    sortIndex: 2,
    inputs: (ports) => [ports.checkboxFieldId(), ports.checkboxState()],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'textbox',
    label: 'Textbox',
    description: 'Represents a textbox in the Wizard',
    initialWidth: 210,
    sortIndex: 3,
    inputs: (ports) => [ports.textboxFieldId()],
    outputs: (ports) => [
      ports.string({ name: 'value', label: 'Current Value' }),
      ports.number({ name: 'charCount', label: 'Character Count' }),
    ],
  })
  .addNodeType({
    type: 'uploadAndSubmit',
    label: 'Upload and Submit',
    description: 'Represents an upload & submit control in the Wizard',
    initialWidth: 210,
    sortIndex: 4,
    inputs: (ports) => [ports.uploadAndSubmitFieldId()],
    outputs: (ports) => [
      ports.boolean({ name: 'isOnline', label: 'Is Filing Online' }),
      ports.boolean({ name: 'isMailing', label: 'Is Printing & Mailing' }),
      ports.boolean({ name: 'isSignatures', label: 'Is Sending for Signatures' }),
    ],
  })
  .addNodeType({
    type: 'file',
    label: 'File Upload',
    description: 'Represents an upload control in the Wizard',
    initialWidth: 210,
    sortIndex: 4.1,
    inputs: (ports) => [ports.uploadFieldId()],
    outputs: (ports) => [ports.number({ name: 'numAttachments', label: 'Number of Attachments' })],
  })
  .addNodeType({
    type: 'dateInput',
    label: 'Date Input',
    description: 'Outputs a date',
    initialWidth: 210,
    sortIndex: 5,
    inputs: (ports) => [ports.dateFieldId()],
    outputs: (ports) => [ports.date({ name: 'date', label: 'Date' })],
  })
  .addNodeType({
    type: 'entityNameField',
    label: 'Entity Name Field',
    description: 'Outputs attributes of an entity name field.',
    initialWidth: 210,
    sortIndex: 5,
    inputs: (ports) => [ports.entityNameFieldId()],
    outputs: (ports) => [
      ports.boolean({ name: 'isReserved', label: 'Is Reserved Name' }),
      ports.string({ name: 'reservedName', label: 'Reserved Name' }),
      ports.string({ name: 'entityName', label: 'Entered Entity Name' }),
      ports.string({ name: 'confirmedEntityName', label: 'Confirmed Entity Name' }),
      ports.boolean({ name: 'additionalNameNeeded', label: 'Additional Name Needed' }),
      ports.string({ name: 'additionalName', label: 'Additional Name' }),
      ports.boolean({ name: 'consentNeeded', label: 'Consent Needed' }),
      ports.number({ name: 'numConsents', label: 'Number of Consents' }),
    ],
  })
  .addNodeType({
    type: 'nameQualifiedOrgField',
    label: 'Name Qualified Org Field',
    description: 'Outputs attributes of a name qualified org field.',
    initialWidth: 210,
    sortIndex: 5.1,
    inputs: (ports) => [ports.nameQualifiedOrgFieldId()],
    outputs: (ports) => [
      ports.boolean({ name: 'isIndividual', label: 'Is Individual' }),
      ports.string({ name: 'firstName', label: 'Individual First Name' }),
      ports.string({ name: 'middleName', label: 'Individual Middle/Initial' }),
      ports.string({ name: 'lastName', label: 'Individual Last Name' }),
      ports.boolean({ name: 'isQualifiedOrg', label: 'Is Qualified Org' }),
      ports.boolean({ name: 'hasSelectedQualifiedOrg', label: 'Has Selected Qualified Org' }),
      ports.boolean({ name: 'isUnqualifiedOrg', label: 'Is Unqualified Org' }),
      ports.string({ name: 'unqualifiedOrg', label: 'Unqualified Org' }),
    ],
  })
  .addNodeType({
    type: 'repeater',
    label: 'Repeater',
    description: 'Outputs attributes of a repeater.',
    initialWidth: 210,
    sortIndex: 5.2,
    inputs: (ports) => [ports.repeaterFieldId()],
    outputs: (ports) => [ports.number({ name: 'numRows', label: 'Number of rows' })],
  })
  .addNodeType({
    type: 'formationLocaleField',
    label: 'Formation Locale Field',
    description: 'Outputs attributes of a formation locale field.',
    initialWidth: 210,
    sortIndex: 5.3,
    inputs: (ports) => [ports.formationLocaleFieldId()],
    outputs: (ports) => [
      ports.boolean({ name: 'isStateType', label: 'Selected "United States"' }),
      ports.boolean({ name: 'isCountryType', label: 'Selected "Country"' }),
      ports.boolean({ name: 'isOtherType', label: 'Selected "Other Jurisdiction' }),
      ports.string({ name: 'STATE', label: 'State' }),
      ports.string({ name: 'COUNTRY', label: 'Country' }),
      ports.string({ name: 'OTHER', label: 'Other Jurisdiction' }),
    ],
  })
  .addNodeType({
    type: 'user',
    label: 'Current User',
    description: 'Outputs attributes of the current user',
    initialWidth: 140,
    sortIndex: 6,
    outputs: (ports) => [
      ports.boolean({ name: 'isLoggedIn', label: 'Is Logged-in' }),
      ports.boolean({ name: 'isAdmin', label: 'Is Admin' }),
      ports.string({ name: 'firstName', label: 'First Name' }),
      ports.string({ name: 'lastName', label: 'Last Name' }),
      ports.string({ name: 'email', label: 'Email' }),
    ],
  })
  .addNodeType({
    type: 'businessRules',
    label: 'Business Rules',
    description: 'Outputs a set of business rules for the current field',
    initialWidth: 200,
    sortIndex: 7,
    inputs: (ports) => [ports.businessRules()],
    outputs: (ports) => [ports.businessRules()],
  })
  .addNodeType({
    type: 'businessRulesSwitch',
    label: 'Business Rules Switch',
    description: 'Outputs a set of business rules based on a true/false',
    initialWidth: 200,
    sortIndex: 8,
    inputs: (ports) => [
      ports.boolean({ name: 'test' }),
      ports.businessRules({ name: 'rules1' }),
      ports.businessRules({ name: 'rules2' }),
    ],
    outputs: (ports) => [ports.businessRules()],
  })
  .addNodeType({
    type: 'metadataValue',
    label: 'Metadata Value',
    description: 'Outputs a specified value from the metadata',
    initialWidth: 200,
    sortIndex: 8.1,
    inputs: (ports) => [ports.string({ name: 'key', label: 'Metadata key' })],
    outputs: (ports) => [ports.string({ name: 'value', label: 'Metadata value' })],
  })
  .addNodeType({
    type: 'booleanAppSetting',
    label: 'True/False App Setting',
    description: 'Outputs the value of a true/false app setting',
    initialWidth: 200,
    sortIndex: 9,
    inputs: (ports) => [ports.booleanAppSetting()],
    outputs: (ports) => [ports.boolean({ name: 'value' })],
  })
  .addNodeType({
    type: 'stringAppSetting',
    label: 'Text App Setting',
    description: 'Outputs the value of a text app setting',
    initialWidth: 200,
    sortIndex: 10,
    inputs: (ports) => [ports.stringAppSetting()],
    outputs: (ports) => [ports.string({ name: 'value' })],
  })
  .addNodeType({
    type: 'numberAppSetting',
    label: 'Number App Setting',
    description: 'Outputs the value of a number app setting',
    initialWidth: 200,
    sortIndex: 11,
    inputs: (ports) => [ports.numberAppSetting()],
    outputs: (ports) => [ports.number({ name: 'value' })],
  })
  .addNodeType({
    type: 'globalBoolean',
    label: 'Global True/False Variable',
    description: 'Outputs the value of a global true/false variable',
    initialWidth: 200,
    sortIndex: 12,
    inputs: (ports) => [ports.globalBoolean({ hidePort: true })],
    outputs: (ports) => [ports.boolean({ name: 'value' })],
  })
  .addNodeType({
    type: 'globalString',
    label: 'Global Text Variable',
    description: 'Outputs the value of a global text variable',
    initialWidth: 200,
    sortIndex: 13,
    inputs: (ports) => [ports.globalString({ hidePort: true })],
    outputs: (ports) => [ports.string({ name: 'value' })],
  })
  .addNodeType({
    type: 'globalNumber',
    label: 'Global Number Variable',
    description: 'Outputs the value of a global number variable',
    initialWidth: 200,
    sortIndex: 14,
    inputs: (ports) => [ports.globalNumber({ hidePort: true })],
    outputs: (ports) => [ports.number({ name: 'value' })],
  })
  .addNodeType({
    type: 'wizardSettings',
    label: 'Wizard Settings',
    description: 'Outputs settings of the current wizard',
    initialWidth: 180,
    sortIndex: 15,
    outputs: (ports) => [
      ports.string({ name: 'title', label: 'Title' }),
      ports.string({ name: 'description', label: 'Description' }),
      ports.string({ name: 'formTitle', label: 'Form Title' }),
      ports.string({ name: 'feeDescription', label: 'Fee Description' }),
    ],
  })
  .addNodeType({
    type: 'boolean',
    label: 'True/False',
    description: 'A boolean value of true or false',
    initialWidth: 150,
    sortIndex: 20,
    inputs: (ports) => [ports.boolean()],
    outputs: (ports) => [ports.boolean()],
  })
  .addNodeType({
    type: 'and',
    label: 'And',
    description: "Outputs true if two provided True/False's are true",
    initialWidth: 150,
    sortIndex: 21,
    inputs: (ports) => [ports.boolean({ name: 'bool1' }), ports.boolean({ name: 'bool2' })],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'or',
    label: 'Or',
    description: 'Outputs true if either provided True/False is true',
    initialWidth: 150,
    sortIndex: 22,
    inputs: (ports) => [ports.boolean({ name: 'bool1' }), ports.boolean({ name: 'bool2' })],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'reverseBoolean',
    label: 'Reverse True/False',
    description: 'Reverses a True/False',
    initialWidth: 150,
    sortIndex: 23,
    inputs: (ports) => [ports.boolean()],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'string',
    label: 'Text',
    description: 'A string of text',
    initialWidth: 200,
    sortIndex: 30,
    inputs: (ports) => [ports.string()],
    outputs: (ports) => [ports.string()],
  })
  .addNodeType({
    type: 'stringSwitch',
    label: 'Text Switch',
    description: 'Returns a string of text based on a true/false',
    initialWidth: 170,
    sortIndex: 31,
    inputs: (ports) => [
      ports.boolean({ name: 'test' }),
      ports.string({ name: 'stringIfTrue', label: 'Text if true' }),
      ports.string({ name: 'stringIfFalse', label: 'Text if false' }),
    ],
    outputs: (ports) => [ports.string({ name: 'result', label: 'Text' })],
  })
  .addNodeType({
    type: 'stringJoin',
    label: 'Join Text',
    description: 'Combines two strings of text into one string.',
    initialWidth: 170,
    sortIndex: 32,
    inputs: (ports) => [
      ports.string({ name: 'string1', label: 'First half of the text' }),
      ports.string({ name: 'string2', label: 'Second half of the text' }),
    ],
    outputs: (ports) => [ports.string({ name: 'result', label: 'Text' })],
  })
  .addNodeType({
    type: 'stringEquals',
    label: 'Text Equals',
    description: 'Outputs if text equals another string of text.',
    initialWidth: 170,
    sortIndex: 33,
    inputs: (ports) => [
      ports.string({ name: 'string1', label: 'First Text' }),
      ports.string({ name: 'string2', label: 'Second Text' }),
      ports.boolean({ name: 'caseSensitive', label: 'Case Sensitive' }),
    ],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'stringContains',
    label: 'Text Contains',
    description: 'Outputs if text contains another string of text.',
    initialWidth: 170,
    sortIndex: 33,
    inputs: (ports) => [
      ports.string({ name: 'string1', label: 'Containing text' }),
      ports.string({ name: 'string2', label: 'Text to look for' }),
      ports.boolean({ name: 'caseSensitive', label: 'Case Sensitive' }),
    ],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'stringRegex',
    label: 'Text Matches Regex',
    description: 'Outputs if text matches a regex string.',
    initialWidth: 170,
    sortIndex: 34,
    inputs: (ports) => [
      ports.string({ name: 'string', label: 'Text to check' }),
      ports.string({ name: 'regex', label: 'Regex' }),
      ports.string({ name: 'flags', label: 'Flags' }),
    ],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'number',
    label: 'Number',
    description: 'An integer or float',
    initialWidth: 150,
    sortIndex: 40,
    inputs: (ports) => [ports.number()],
    outputs: (ports) => [ports.number()],
  })
  .addNodeType({
    type: 'addNumbers',
    label: 'Add Numbers',
    description: 'Add together two numbers',
    initialWidth: 150,
    sortIndex: 41,
    inputs: (ports) => [ports.number({ name: 'num1' }), ports.number({ name: 'num2' })],
    outputs: (ports) => [ports.number({ name: 'result' })],
  })
  .addNodeType({
    type: 'subtractNumbers',
    label: 'Subtract Numbers',
    description: 'Subtract one number from another',
    initialWidth: 150,
    sortIndex: 42,
    inputs: (ports) => [ports.number({ name: 'num1' }), ports.number({ name: 'num2' })],
    outputs: (ports) => [ports.number({ name: 'result' })],
  })
  .addNodeType({
    type: 'multiplyNumbers',
    label: 'Multiply Numbers',
    description: 'Multiply one number by another',
    initialWidth: 150,
    sortIndex: 43,
    inputs: (ports) => [ports.number({ name: 'num1' }), ports.number({ name: 'num2' })],
    outputs: (ports) => [ports.number({ name: 'result' })],
  })
  .addNodeType({
    type: 'divideNumbers',
    label: 'Divide Numbers',
    description: 'Divide one number by another',
    initialWidth: 150,
    sortIndex: 44,
    inputs: (ports) => [ports.number({ name: 'num1' }), ports.number({ name: 'num2' })],
    outputs: (ports) => [ports.number({ name: 'result' })],
  })
  .addNodeType({
    type: 'greaterThanNumber',
    label: 'Greater Than',
    description: 'Outputs if one number is greater than another',
    initialWidth: 150,
    sortIndex: 45,
    inputs: (ports) => [
      ports.number({ name: 'num1', label: 'Number to compare' }),
      ports.number({ name: 'num2', label: 'Is greater than' }),
    ],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'lessThanNumber',
    label: 'Less Than',
    description: 'Outputs if one number is less than another',
    initialWidth: 150,
    sortIndex: 46,
    inputs: (ports) => [
      ports.number({ name: 'num1', label: 'Number to compare' }),
      ports.number({ name: 'num2', label: 'Is less than' }),
    ],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'numbersEqual',
    label: 'Number Equals',
    description: 'Outputs if one number equals another number',
    initialWidth: 150,
    sortIndex: 47,
    inputs: (ports) => [ports.number({ name: 'num1' }), ports.number({ name: 'num2' })],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'numberToString',
    label: 'Number To Text',
    description: 'Converts a number to text',
    initialWidth: 150,
    sortIndex: 48,
    inputs: (ports) => [ports.number({ name: 'num' })],
    outputs: (ports) => [ports.string({ name: 'result' })],
  })
  .addNodeType({
    type: 'stringToNumber',
    label: 'Text To Number',
    description: 'Attempts to convert text to a number. Defaults to 0 if conversion fails.',
    initialWidth: 160,
    sortIndex: 48,
    inputs: (ports) => [ports.string({ name: 'string' })],
    outputs: (ports) => [
      ports.number({ name: 'result' }),
      ports.boolean({ name: 'succeeded', label: 'Conversion Succeeded' }),
    ],
  })
  .addNodeType({
    type: 'date',
    label: 'Date',
    description: 'Outputs a date',
    initialWidth: 160,
    sortIndex: 50,
    inputs: (ports) => [ports.date()],
    outputs: (ports) => [ports.date()],
  })
  .addNodeType({
    type: 'isBeforeDate',
    label: 'Is Before Date',
    description: 'Outputs if a date is before another date',
    initialWidth: 160,
    sortIndex: 51,
    inputs: (ports) => [
      ports.date({ name: 'date', label: 'Date to Compare' }),
      ports.date({ name: 'isBeforeDate', label: 'Date is Before' }),
    ],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'isAfterDate',
    label: 'Is After Date',
    description: 'Outputs if a date is after another date',
    initialWidth: 160,
    sortIndex: 52,
    inputs: (ports) => [
      ports.date({ name: 'date', label: 'Date to Compare' }),
      ports.date({ name: 'isAfterDate', label: 'Date is After' }),
    ],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })
  .addNodeType({
    type: 'datesEqual',
    label: 'Dates equal',
    description: 'Outputs if a date is the same as another date',
    initialWidth: 160,
    sortIndex: 53,
    inputs: (ports) => [
      ports.date({ name: 'date1', label: 'Date' }),
      ports.date({ name: 'date2', label: 'Date' }),
    ],
    outputs: (ports) => [ports.boolean({ name: 'result' })],
  })

export default config

export const defaultFieldNodes = [
  {
    x: 355,
    y: -190,
    type: 'output',
    width: 170,
  },
]



// WEBPACK FOOTER //
// ./src/components/Designer2/logicTypes/fieldFlumeConfig.js